import { Input, DatePicker, Switch, message } from "antd";
import scheduleIcon from ".././../assets/icons/schedule-icon.png";
import MainLayout from "../../layout/MainLayout";
import swiftIcon from "../../assets/temp-icons/swift-icon.png";
import fedwireIcon from "../../assets/temp-icons/fedwire.png";
import Select from "../select/Select";
import ToggleButton from "../togglebutton/ToggleButton";
import { Button1, Button2 } from "../buttons/Buttons";
import Radio from "../radio/Radio";
import TagInput from "../tag-input/TagInput";
import QuickActionSelect from "../quick-action-select/QuickActionSelect";

import plusIcon from "../../assets/icons/plus.png";
import plusIconWhite from "../../assets/icons/plus-circle-white.png";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAccounts, useBeneficiaries } from "../../data/live";
import { useAuth } from "../../api/useAuth";
import infoIcon from "../../assets/icons/info-icon.png";
import { transfersApi } from "../../api/endpoints";

const last4 = (s) => (s ? String(s).slice(-4) : "0000");
const COUNTRIES = [
  { name: "Australia", value: "AU" },
  { name: "United States", value: "US" },
  { name: "United Kingdom", value: "GB" },
  { name: "Pakistan", value: "PK" },
  { name: "India", value: "IN" },
  { name: "Canada", value: "CA" },
];
const PURPOSES = [
  "Family Support",
  "Salary",
  "Bill Payment",
  "Education",
  "Medical",
  "Business",
  "Freight",
  "Other",
];
const CATEGORIES = ["Family", "Friend", "Vendor", "Employee", "Business"];
const TRANSFER_METHODS = [
  { icon: swiftIcon, name: "SWIFT" },
  { icon: fedwireIcon, name: "Fedwire" },
];
const FREQUENCIES = [
  { name: "Once", value: "ONCE" },
  { name: "Weekly", value: "WEEKLY" },
  { name: "Monthly", value: "MONTHLY" },
  { name: "Yearly", value: "YEARLY" },
];

const ScheduleYourTransfer = () => {
  const navigate = useNavigate();
  const { accounts } = useAccounts();
  const { beneficiaries } = useBeneficiaries();
  const { user } = useAuth();

  const [paymentType, setPaymentType] = useState("Bill Payment");
  const [isScheduled, setIsScheduled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [quote, setQuote] = useState(null);

  const [form, setForm] = useState({
    sourceAccountId: "",
    beneficiaryId: "",
    sourceAmount: "",
    destCurrency: "USD",
    destCountry: "AU",
    accountType: "INDIVIDUAL",
    transferMethodIndex: 0,
    feeShare: "Fees are Shared",
    purpose: "",
    referenceNote: "",
    internalRef: "",
    category: "",
    scheduleDate: null,
    frequency: "ONCE",
    endDate: null,
  });

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (accounts.length > 0 && !form.sourceAccountId) {
      const def =
        accounts.find((a) => a.isDefault) ||
        accounts.find((a) => a.currency === "AUD") ||
        accounts[0];
      update("sourceAccountId", def.id);
    }
  }, [accounts, form.sourceAccountId]);

  const sourceAccount = accounts.find((a) => a.id === form.sourceAccountId);
  const userName = user
    ? `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email
    : "Account";
  const accountOptions = accounts.map((a) => ({
    name: `${a.currency} • ****${last4(a.accountNumber || a.iban)}${
      a.isDefault ? " (Default)" : ""
    }`,
    value: a.id,
  }));

  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (
      !form.sourceAccountId ||
      !form.sourceAmount ||
      isNaN(Number(form.sourceAmount)) ||
      Number(form.sourceAmount) <= 0
    ) {
      setQuote(null);
      return;
    }
    debounceRef.current = setTimeout(() => {
      transfersApi
        .quote({
          sourceAccountId: form.sourceAccountId,
          sourceAmount: Number(form.sourceAmount),
          destCurrency: form.destCurrency,
          transferType: "INTERNATIONAL",
        })
        .then(setQuote)
        .catch(() => setQuote(null));
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [form.sourceAccountId, form.sourceAmount, form.destCurrency]);

  const handleContinue = () => {
    setError(null);
    if (!form.sourceAccountId) {
      setError("Please select a source account");
      return;
    }
    if (!form.beneficiaryId) {
      setError("Please select a recipient");
      return;
    }
    if (!form.sourceAmount || Number(form.sourceAmount) <= 0) {
      setError("Enter a valid amount");
      return;
    }
    if (isScheduled && !form.scheduleDate) {
      setError("Pick a start date for schedule");
      return;
    }
    setLoading(true);
    try {
      navigate("/schedule-transfer-review", {
        state: { form, isScheduled, sourceAccount, quote, paymentType },
      });
    } finally {
      setLoading(false);
    }
  };

  const beneficiaryOptions = beneficiaries.map((b) => ({
    name: `${b.fullName || b.businessName} (${b.countryCode || ""})`,
    value: b.id,
  }));

  const availableBalance = sourceAccount
    ? `${Number(sourceAccount.availableBalance).toLocaleString()} ${
        sourceAccount.currency
      }`
    : "0.00";

  const onCardSelect = (key) => update("transferMethodIndex", key);

  return (
    <>
      <MainLayout
        expand={false}
        NavName="Schedule Transfer"
        navbarChildren={<QuickActionSelect />}
        pageClassName="dashboard-page schedule-transfer-page"
        contentClassName="dashboard-content"
      >
        <div className="schedule-transfer-shell">
            <div className="schedule-transfer-overview">
              <div>
                <span className="dashboard-eyebrow">Payment planning</span>
                <h2>Schedule Transfer</h2>
                <p>Set up one-time or recurring transfers with recipient, timing, and reference details.</p>
              </div>
              <Button2
                buttonName={"Add Funds"}
                className={"btn-outline-success"}
                divClassName={"gap-1"}
                icon={<img src={plusIcon} alt="add" />}
                onClick={() => navigate("/add-funds")}
              />
            </div>

            <div className="schedule-transfer-topbar d-flex flex-column gap-3 mt-4">
              <Select
                value={form.sourceAccountId}
                onChange={(e) => update("sourceAccountId", e.target.value)}
                options={accountOptions}
                defaultValue="Select account"
                selectStyle={{ width: "100%", maxWidth: "320px" }}
                className="bg-light rounded-5 schedule-transfer-select"
                popupClassName="bg-White"
              />
              <div className="d-flex  justify-content-between flex-wrap gap-3">
                <div className="d-flex gap-3 align-items-center justify-content-between justify-content-md-start">
                  <span className="fs-5  fw-medium">New Schedule Transfer</span>
                </div>

                <div className="d-flex  gap-3 align-items-center flex-wrap flex-md-nowrap justify-content-center justify-content-md-start">
                  <p className="p-0 m-0 fs14px">
                    Available balance:{" "}
                    <span
                      className="fs-6 fw-medium"
                      style={{ color: "#1bbb6b" }}
                    >
                      {availableBalance}
                    </span>
                  </p>

                </div>
              </div>
            </div>

            <div className="schedule-transfer-card row p-3 mt-4 justify-content-between">
              <div className="d-flex align-items-center justify-content-center">
                <ToggleButton
                  defaultValue={paymentType}
                  options={[
                    "Local Payment",
                    "Bill Payment",
                    "International Payment",
                  ]}
                  onChange={setPaymentType}
                />
              </div>

              <div className="col-lg-5 p-3">
                <div className="schedule-transfer-panel d-flex flex-column gap-3">
                  <span className="fs-5 fw-medium">Schedule Transfer</span>

                  <div className="d-flex flex-column gap-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <label className="fs12px">Select Recipient</label>
                      <Button2
                        icon={<img src={plusIconWhite} alt="add" />}
                        buttonName={"New Recipients"}
                        className={"btn-dark px-2 py-1"}
                        divClassName={"fs14px gap-2"}
                        onClick={() => navigate("/add-recipient")}
                      />
                    </div>

                    <Select
                      value={form.beneficiaryId}
                      defaultValue="Select recipient"
                      className="w-100"
                      options={beneficiaryOptions}
                      onChange={(e) =>
                        update("beneficiaryId", e.target.value)
                      }
                    />
                  </div>

                  <div className="d-flex flex-column gap-2">
                    <div className="d-flex gap-3 ">
                      <div className="w-75">
                        <label className="fs12px">You send</label>
                        <Input
                          className="p-3 w-100"
                          placeholder="0.00"
                          value={form.sourceAmount}
                          onChange={(e) =>
                            update("sourceAmount", e.target.value)
                          }
                        />
                      </div>
                      <div>
                        <label className="fs12px">Currency</label>
                        <Select
                          value={sourceAccount?.currency || "AUD"}
                          options={[
                            {
                              name: sourceAccount?.currency || "AUD",
                              value: sourceAccount?.currency || "AUD",
                            },
                          ]}
                          className="p-3 "
                          selectStyle={{ width: "100%" }}
                          disabled
                        />
                      </div>
                    </div>
                    <span
                      className="fs12px fw-medium"
                      style={{ color: "#7D7D7D" }}
                    >
                      Available balance :{" "}
                      <span className="text-black">{availableBalance}</span>
                    </span>
                  </div>

                  <div className="d-flex flex-column gap-2">
                    <label className="fs12px">Transfer to</label>
                    <Select
                      value={form.destCountry}
                      options={COUNTRIES}
                      defaultValue="Select country"
                      className="p-3"
                      onChange={(e) => update("destCountry", e.target.value)}
                    />
                  </div>

                  <div className=" d-flex flex-column gap-2">
                    <span className="fs12px">Recipients type</span>
                    <div className="d-flex justify-content-between  ">
                      <div className="d-flex gap-2 align-items-center w-50">
                        <Radio
                          name={"sched-account-type"}
                          accentColor={"green"}
                          height={"20px"}
                          width={"20px"}
                          checked={form.accountType === "BUSINESS"}
                          onClick={() => update("accountType", "BUSINESS")}
                        />
                        <span className="fs14px">Business</span>
                      </div>
                      <div className="d-flex gap-2 align-items-center w-50 justify-content-end justify-content-md-start">
                        <Radio
                          name={"sched-account-type"}
                          accentColor={"green"}
                          height={"20px"}
                          width={"20px"}
                          checked={form.accountType === "INDIVIDUAL"}
                          onClick={() => update("accountType", "INDIVIDUAL")}
                        />
                        <span className="fs14px">Individual</span>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex flex-column gap-2">
                    <span className="fs12px" style={{ color: "#7D7D7D" }}>
                      Transfer method
                    </span>
                    <div className="d-flex flex-wrap gap-2">
                      {TRANSFER_METHODS.map(({ name, icon }, key) => (
                        <div
                          key={key}
                          className={`d-flex justify-content-between transfer-method-card rounded-3 ${
                            key === form.transferMethodIndex
                              ? "selected-card"
                              : "border"
                          }  p-3`}
                        >
                          <div className="d-flex gap-2 align-items-center">
                            <img src={icon} alt="icon" width={25} />
                            <span className="fs14px fw-medium">{name}</span>
                          </div>
                          <Radio
                            height={"20px"}
                            width={"24px"}
                            accentColor={"green"}
                            name={"sched-transfer-method"}
                            checked={key === form.transferMethodIndex}
                            onClick={() => onCardSelect(key)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="d-flex flex-column gap-2">
                    <Select
                      value={form.feeShare}
                      options={[
                        { name: "Fees are Shared", value: "Fees are Shared" },
                        { name: "Sender pays all", value: "Sender pays all" },
                        {
                          name: "Recipient pays all",
                          value: "Recipient pays all",
                        },
                      ]}
                      defaultValue="Fees are Shared"
                      className="p-3"
                      onChange={(e) => update("feeShare", e.target.value)}
                    />
                    <div className="d-flex gap-2 align-items-center">
                      <img
                        src={infoIcon}
                        alt="info"
                        className="align-self-start"
                      />
                      <span
                        className="fs12px fw-medium"
                        style={{ color: "#7D7D7D" }}
                      >
                        You pay the outgoing transfer fees and the recipients
                        pays the and receiving bank fees
                      </span>
                    </div>
                  </div>

                  <div className="d-flex flex-column gap-2">
                    <div className="d-flex gap-3 ">
                      <div className="w-75">
                        <label className="fs12px">Recipients get</label>
                        <Input
                          className="p-3 w-100"
                          placeholder="0.00"
                          value={quote?.destAmount || ""}
                          readOnly
                        />
                      </div>
                      <div>
                        <label className="fs12px">Currency</label>
                        <Select
                          value={form.destCurrency}
                          options={["USD", "EUR", "GBP", "PKR", "INR"].map(
                            (c) => ({ name: c, value: c })
                          )}
                          className="p-3 "
                          selectStyle={{ width: "100%" }}
                          onChange={(e) =>
                            update("destCurrency", e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="d-flex flex-column gap-3 ">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex gap-3 align-items-center">
                        <img src={scheduleIcon} alt="schedule" />
                        <div className="d-flex flex-column">
                          <span className="fw-medium">Schedule</span>
                          <span
                            className="fs12px fw-medium"
                            style={{ color: "#7D7D7D" }}
                          >
                            or set up recurring transfer
                          </span>
                        </div>
                      </div>
                      <Switch
                        checked={isScheduled}
                        onChange={setIsScheduled}
                      />
                    </div>
                    {isScheduled && (
                      <div className="d-flex flex-column gap-3 mt-4">
                        <div className="d-flex flex-column gap-2">
                          <label className="fs12px">Date</label>
                          <DatePicker
                            className="p-3"
                            disabled={!isScheduled}
                            onChange={(d) =>
                              update("scheduleDate", d?.toISOString() || null)
                            }
                          />
                        </div>
                        <div className="d-flex flex-column gap-2">
                          <label className="fs12px">Repeat</label>
                          <Select
                            value={form.frequency}
                            options={FREQUENCIES}
                            defaultValue="Once"
                            className="p-3"
                            disabled={!isScheduled}
                            onChange={(e) => update("frequency", e.target.value)}
                          />
                        </div>
                        <div className="d-flex flex-column gap-2">
                          <label className="fs12px">End date</label>
                          <DatePicker
                            className="p-3"
                            disabled={!isScheduled}
                            onChange={(d) =>
                              update("endDate", d?.toISOString() || null)
                            }
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-6 p-3">
                <div className="schedule-transfer-panel d-flex flex-column gap-3">
                  <span className="fs-5 fw-medium"> Transfer Details</span>

                  <div className="d-flex flex-column gap-2">
                    <label className="fs12px">Purpose</label>
                    <Select
                      value={form.purpose}
                      options={PURPOSES.map((p) => ({ name: p, value: p }))}
                      defaultValue="Select purpose"
                      className="p-3"
                      onChange={(e) => update("purpose", e.target.value)}
                    />
                    <span className="fs12px" style={{ color: "#7D7D7D" }}>
                      Please Specify purpose to ensure timely dispatch of
                      transfer
                    </span>
                  </div>

                  <div className="d-flex flex-column gap-2">
                    <label className="fs12px">Transfer reference</label>
                    <Input
                      placeholder="Payment for invoice #983263"
                      className="p-3"
                      value={form.referenceNote}
                      onChange={(e) =>
                        update("referenceNote", e.target.value)
                      }
                    />
                    <span className="fs12px" style={{ color: "#7D7D7D" }}>
                      Displayed on recipient's bank statement
                    </span>
                  </div>

                  <div className="d-flex flex-column gap-2">
                    <label className="fs12px">
                      Internal reference{" "}
                      <span style={{ color: "#7D7D7D" }}>(Optional)</span>
                    </label>
                    <Input
                      placeholder="Job no #293736"
                      className="p-3"
                      value={form.internalRef}
                      onChange={(e) =>
                        update("internalRef", e.target.value)
                      }
                    />
                    <span className="fs12px" style={{ color: "#7D7D7D" }}>
                      We won't send this to recipient. It needs to be district
                      over the last 7 days
                    </span>
                  </div>

                  <div className="d-flex flex-column gap-2 mt-4">
                    <span className="fs-5 fw-bold">Additional Details</span>
                    <div className="d-flex flex-column gap-2 mt-4">
                      <label className="fs12px ">Category</label>
                      <Select
                        value={form.category}
                        options={CATEGORIES.map((c) => ({
                          name: c,
                          value: c,
                        }))}
                        defaultValue="Choose Category"
                        className="rounded-2 p-3"
                        onChange={(e) => update("category", e.target.value)}
                      />
                    </div>
                    <div className="d-flex flex-column gap-2 mt-4">
                      <label className="fs12px ">Tags</label>
                      <TagInput
                        className="border py-3"
                        inputBgColor="transparent"
                        tagColor="#1bbb6b"
                        tagTextColor="#000"
                        limit={10}
                        placeholder="Add up to 10 Tags"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {error && (
                <div className="col-12 p-2">
                  <div className="fs12px" style={{ color: "#FC555B" }}>
                    {error}
                  </div>
                </div>
              )}

              <div className="d-flex justify-content-md-end p-2 align-items-center flex-wrap justify-content-center gap-3">
                <Button1
                  buttonName={"Save as Draft"}
                  className={"btn-outline-success fs14px"}
                />
                <Button1
                  buttonName={loading ? "Processing..." : "Continue"}
                  className="btn-dark fs14px "
                  onClick={handleContinue}
                  disabled={loading}
                />
              </div>
            </div>
        </div>
      </MainLayout>
    </>
  );
};

export default ScheduleYourTransfer;
